import { FieldSet, InputWizardPanelComponent, SelectComponent, TextInput } from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { RegionSelect } from "../../components/RegionSelect/RegionSelect";
import { ResourceGroupSelect } from "../../components/ResourceGroupSelect/ResourceGroupSelect";
import { SubscriptionSelect } from "../../components/SubscriptionSelect/SubscriptionSelect";
import { nameCharMaxLength, nameCharMinLength, nameValidationRegex } from "../../helpers/validationHelper";

export const GROUP_BASICS = "basics";

export enum Fields {
  Subscription = "subscriptionId",
  ResourceGroup = "resourceGroupId",
  Name = "cloudExadataInfrastructureDetails.name",
  Region = "region",
}

export enum FieldTestIds {
  Subscription = "subscription",
  Name = "name",
  ResourceGroup = "resource-group",
  Region = "region",
}

export interface BasicsTabComponent {
  closeSidePanels: () => void;
}
export interface BasicsTabContentProps {
  inputWizardPanelRef: InputWizardPanelComponent;
  componentRef?: (component: BasicsTabComponent) => void;
  subscription: string;
  location: string;
}

export const BasicsTabContent = (
  { inputWizardPanelRef, subscription, location }: BasicsTabContentProps,
): JSX.Element => {
  const [subscriptionId, setSubscriptionId] = React.useState<string>(subscription);

  const resourceSelectRef = React.useRef<SelectComponent>();
  const setResourceSelectRef = (selectComponent: SelectComponent): void => {
    resourceSelectRef.current = selectComponent;
  };

  const onSubscriptionChange = (subId: string): void => setSubscriptionId(subId);

  const onResourceGroupError = (): void => {
    inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.resourceGroups());
  };

  const onResourceGroupMissingDependencies = (): void => inputWizardPanelRef.showErrorDialog(
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.message(),
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.title(),
  );

  return (
    <>
      <FieldSet header={Messages.createInfra.basicsTab.sectionTitles.projectDetails()}>
        <SubscriptionSelect
          required
          testId={FieldTestIds.Subscription}
          onChange={onSubscriptionChange}
          fieldName={Fields.Subscription}
          label={Messages.labels.subscription()}
          tooltip={Messages.hints.toolTipSubscription()}
          defaultValue={subscriptionId ? [subscriptionId] : undefined}
        />
        <ResourceGroupSelect
          required
          subField
          testId={FieldTestIds.ResourceGroup}
          subscriptionId={subscriptionId}
          componentRef={setResourceSelectRef}
          fieldName={Fields.ResourceGroup}
          label={Messages.labels.resourceGroup()}
          tooltip={Messages.hints.toolTipResourceGroup()}
          onError={onResourceGroupError}
          onMissingDependencies={onResourceGroupMissingDependencies}
        />
      </FieldSet>
      <FieldSet header={Messages.createInfra.basicsTab.sectionTitles.instanceDetails()}>
        <TextInput
          required
          testId={FieldTestIds.Name}
          fieldName={Fields.Name}
          label={Messages.labels.name()}
          validator={(value: string | undefined) => {
            const errors: string[] = [];
            if (!value || (value?.length < nameCharMinLength || value?.length > nameCharMaxLength)) {
              errors.push(Messages.validation.nameChars(nameCharMinLength.toString(), nameCharMaxLength.toString()));
            }
            if (!value || (value && !nameValidationRegex.test(value))) {
              errors.push(Messages.validation.nameValidation());
            }
            return errors.length > 0 ? errors : undefined;
          }}
        />
        <RegionSelect
          required
          subscriptionId={subscriptionId}
          // showUnlinkedRegions
          fieldName={Fields.Region}
          testId={FieldTestIds.Region}
          label={Messages.labels.region()}
          tooltip={Messages.hints.toolTipRegion()}
          defaultValue={location ? [location] : undefined}
        />
      </FieldSet>
    </>
  );
};
